import { useState, useEffect } from 'react'
import type { NetworkingRequest } from '../../api/types'
import { useNetworkingCalc } from '../../hooks/useCalculate'
import { useAnalytics } from '../../hooks/useAnalytics'
import { CurrencyTooltip } from '../shared/CurrencyTooltip'
import { FormulaDisclosure } from '../shared/FormulaDisclosure'
import { DiscountModifier } from '../shared/DiscountModifier'
import { MobileCostBar } from '../shared/MobileCostBar'
import { FeedbackModal } from '../shared/FeedbackModal'
import { useCalculatorStore } from '../../store/calculatorStore'
import { FieldTooltip } from '../shared/FieldTooltip'
import { Slider } from '../ui/Slider'
import { Toggle } from '../ui/Toggle'

const defaultInputs: NetworkingRequest = {
  natGateway: {
    enabled: false,
    hoursPerMonth: 744,
    dataProcessedGB: 0,
  },
  vnetPeering: {
    enabled: false,
    samePeeringInboundGB: 0,
    samePeeringOutboundGB: 0,
    globalPeeringGB: 0,
  },
  publicIPs: {
    staticCount: 0,
    dynamicAttachedCount: 0,
    dynamicUnattachedCount: 0,
  },
  ddos: {
    tier: 'none',
    protectedIPCount: 1,
  },
}

// NAT Gateway idle monthly cost at UK South retail rates
const NAT_IDLE_MONTHLY = 33.4

export default function NetworkingCalc() {
  const [inputs, setInputs] = useState<NetworkingRequest>(defaultInputs)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const { discount, setDiscount } = useCalculatorStore()

  const { data: calculation, isLoading, isError } = useNetworkingCalc(inputs)
  const { trackCalculatorUsed } = useAnalytics()
  useEffect(() => {
    if (calculation?.monthly_estimate) {
      trackCalculatorUsed({ calculator: 'networking', result_gbp: calculation.monthly_estimate })
    }
  }, [calculation?.monthly_estimate])

  function updateNat<K extends keyof NetworkingRequest['natGateway']>(
    key: K, value: NetworkingRequest['natGateway'][K],
  ) {
    setInputs((prev) => ({
      ...prev,
      natGateway: { ...prev.natGateway, [key]: value },
    }))
  }

  function updateVnet<K extends keyof NetworkingRequest['vnetPeering']>(
    key: K, value: NetworkingRequest['vnetPeering'][K],
  ) {
    setInputs((prev) => ({
      ...prev,
      vnetPeering: { ...prev.vnetPeering, [key]: value },
    }))
  }

  function updateIPs<K extends keyof NetworkingRequest['publicIPs']>(
    key: K, value: NetworkingRequest['publicIPs'][K],
  ) {
    setInputs((prev) => ({
      ...prev,
      publicIPs: { ...prev.publicIPs, [key]: value },
    }))
  }

  function updateDDoS<K extends keyof NetworkingRequest['ddos']>(
    key: K, value: NetworkingRequest['ddos'][K],
  ) {
    setInputs((prev) => ({
      ...prev,
      ddos: { ...prev.ddos, [key]: value },
    }))
  }

  // hubSpokeWarning is returned by the networking API but not in the shared CalculationResponse type
  const hubSpokeWarning = (calculation as { hubSpokeWarning?: boolean } | undefined)?.hubSpokeWarning
    ?? (inputs.natGateway.enabled && inputs.vnetPeering.enabled)

  return (
    <div className={`max-w-7xl mx-auto px-6 py-10 pb-20 md:pb-10 grid grid-cols-1 md:grid-cols-[1fr_380px] gap-8 transition-opacity ${isLoading ? 'opacity-70' : ''}`}>

      {/* ── Left column: inputs ─────────────────────────────────────── */}
      <div className="flex flex-col gap-6">

        {/* ── Section 1: NAT Gateway ─────────────────────────────────── */}
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-headline font-bold text-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-[#bc5b00]" style={{ fontVariationSettings: "'FILL' 1" }}>router</span>
              NAT Gateway
            </h2>
            <Toggle
              checked={inputs.natGateway.enabled}
              onChange={(checked) => updateNat('enabled', checked)}
              label={inputs.natGateway.enabled ? 'Enabled' : 'Disabled'}
            />
          </div>

          {inputs.natGateway.enabled && (
            <>
              {/* Always-on amber warning */}
              <div className="mb-5 p-4 bg-[#e8b000]/10 border border-[#e8b000]/40 rounded-sm flex items-start gap-3">
                <span className="material-symbols-outlined text-[#e8b000] text-[18px] shrink-0 mt-0.5" style={{ fontVariationSettings: "'FILL' 1" }}>warning</span>
                <div className="flex flex-col gap-1">
                  <p className="text-[12px] font-semibold text-[#e8b000]">NAT Gateway has no pause or stop state</p>
                  <p className="text-[11px] text-on-surface-variant leading-relaxed">
                    It charges every hour until <span className="font-bold">deleted</span>. An idle NAT Gateway costs approximately{' '}
                    <span className="font-bold font-mono">£{NAT_IDLE_MONTHLY.toFixed(2)}/month</span> for doing nothing.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-5">
                {/* Hours per month */}
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-end">
                    <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Hours per month</label>
                    <span className="text-lg font-bold font-mono text-on-surface">{inputs.natGateway.hoursPerMonth.toLocaleString()} hrs</span>
                  </div>
                  <Slider
                    value={inputs.natGateway.hoursPerMonth}
                    onChange={(value) => updateNat('hoursPerMonth', value)}
                    min={0}
                    max={744}
                    step={1}
                    label="Hours per month"
                    formatValue={(n) => `${n.toLocaleString()} hrs`}
                  />
                  <p className="text-[10px] text-outline italic">744 = always on (full month). NAT Gateway cannot be paused.</p>
                </div>

                {/* Data processed */}
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-end">
                    <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Data processed / month</label>
                    <span className="text-lg font-bold font-mono text-on-surface">{inputs.natGateway.dataProcessedGB.toLocaleString()} GB</span>
                  </div>
                  <Slider
                    value={inputs.natGateway.dataProcessedGB}
                    onChange={(value) => updateNat('dataProcessedGB', value)}
                    min={0}
                    max={10000}
                    step={10}
                    label="Data processed / month"
                    formatValue={(n) => `${n.toLocaleString()} GB`}
                  />
                </div>
              </div>
            </>
          )}

          {!inputs.natGateway.enabled && (
            <p className="text-[11px] text-outline italic">Enable to see NAT Gateway costs. Note: NAT Gateway is always-on — it bills every hour until deleted.</p>
          )}
        </div>

        {/* ── Section 2: VNet Peering ────────────────────────────────── */}
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-headline font-bold text-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>device_hub</span>
              VNet Peering
            </h2>
            <Toggle
              checked={inputs.vnetPeering.enabled}
              onChange={(checked) => updateVnet('enabled', checked)}
              label={inputs.vnetPeering.enabled ? 'Enabled' : 'Disabled'}
            />
          </div>

          {inputs.vnetPeering.enabled && (
            <>
              {/* Free inter-AZ note */}
              <div className="mb-5 p-3 bg-surface-container-lowest/60 border border-outline-variant/50 rounded-sm flex items-start gap-2">
                <span className="material-symbols-outlined text-[#107c10] text-[16px] shrink-0 mt-0.5">info</span>
                <p className="text-[11px] text-outline leading-snug italic">
                  Inter-Availability Zone transfer within UK South is <span className="font-bold text-[#107c10]">free</span> (updated 2023). Only peering between VNets is charged.
                </p>
              </div>

              <div className="flex flex-col gap-5">
                <div>
                  <p className="text-[11px] font-bold text-outline uppercase tracking-wider mb-3">Same-region peering (UK South ↔ UK South)</p>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                      <div className="flex justify-between items-end">
                        <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Inbound GB/month</label>
                        <span className="text-base font-bold font-mono text-on-surface">{inputs.vnetPeering.samePeeringInboundGB.toLocaleString()} GB</span>
                      </div>
                      <Slider
                        value={inputs.vnetPeering.samePeeringInboundGB}
                        onChange={(value) => updateVnet('samePeeringInboundGB', value)}
                        min={0}
                        max={5000}
                        step={10}
                        label="Inbound GB/month"
                        formatValue={(n) => `${n.toLocaleString()} GB`}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex justify-between items-end">
                        <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Outbound GB/month</label>
                        <span className="text-base font-bold font-mono text-on-surface">{inputs.vnetPeering.samePeeringOutboundGB.toLocaleString()} GB</span>
                      </div>
                      <Slider
                        value={inputs.vnetPeering.samePeeringOutboundGB}
                        onChange={(value) => updateVnet('samePeeringOutboundGB', value)}
                        min={0}
                        max={5000}
                        step={10}
                        label="Outbound GB/month"
                        formatValue={(n) => `${n.toLocaleString()} GB`}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-[11px] font-bold text-outline uppercase tracking-wider mb-3">Global peering (UK South ↔ other regions)</p>
                  <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-end">
                      <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Cross-region GB/month</label>
                      <span className="text-base font-bold font-mono text-on-surface">{inputs.vnetPeering.globalPeeringGB.toLocaleString()} GB</span>
                    </div>
                    <Slider
                      value={inputs.vnetPeering.globalPeeringGB}
                      onChange={(value) => updateVnet('globalPeeringGB', value)}
                      min={0}
                      max={5000}
                      step={10}
                      label="Cross-region GB/month"
                      formatValue={(n) => `${n.toLocaleString()} GB`}
                    />
                    <p className="text-[10px] text-outline italic">Charged for both ingress and egress at global peering rate.</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {!inputs.vnetPeering.enabled && (
            <p className="text-[11px] text-outline italic">Enable to calculate VNet peering charges. Inter-AZ within UK South is free.</p>
          )}
        </div>

        {/* Hub-spoke warning */}
        {hubSpokeWarning && (
          <div className="p-4 bg-[#e8b000]/10 border border-[#e8b000]/40 rounded-sm flex items-start gap-3">
            <span className="material-symbols-outlined text-[#e8b000] text-[20px] shrink-0 mt-0.5" style={{ fontVariationSettings: "'FILL' 1" }}>warning</span>
            <div className="flex flex-col gap-1">
              <p className="text-[13px] font-bold text-[#e8b000]">Hub-spoke topology detected</p>
              <p className="text-[12px] text-on-surface-variant leading-relaxed">
                In hub-spoke architectures with an NVA (Network Virtual Appliance), traffic is charged for VNet peering
                twice — once inbound to the hub and once outbound. Your actual peering costs may be{' '}
                <span className="font-bold">2× the estimate shown</span>. Verify your routing topology before using this estimate.
              </p>
            </div>
          </div>
        )}

        {/* ── Section 3: Public IP Addresses ────────────────────────── */}
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-4">
            <span className="material-symbols-outlined text-[#0078d4]" style={{ fontVariationSettings: "'FILL' 1" }}>language</span>
            Public IP Addresses
          </h2>

          <div className="mb-4 p-3 bg-surface-container-lowest/60 border border-outline-variant/50 rounded-sm flex items-start gap-2">
            <span className="material-symbols-outlined text-outline text-[15px] shrink-0 mt-0.5">info</span>
            <p className="text-[11px] text-outline leading-snug">
              Basic SKU retired September 2025. All new Public IPs use Standard SKU.
              First dynamic IP attached to a running resource is free.
            </p>
          </div>

          <div className="flex flex-col gap-5">
            {[
              { key: 'staticCount' as const, label: 'Static Public IPs', note: 'Always charged' },
              { key: 'dynamicAttachedCount' as const, label: 'Dynamic IPs (attached)', note: 'First one free when attached to running resource' },
              { key: 'dynamicUnattachedCount' as const, label: 'Dynamic IPs (idle / unattached)', note: 'Charged even when not associated with a resource' },
            ].map(({ key, label, note }) => (
              <div key={key} className="flex flex-col gap-2">
                <div className="flex justify-between items-end">
                  <label className="text-[11px] font-mono text-outline uppercase tracking-wider">{label}</label>
                  <span className="text-lg font-bold font-mono text-on-surface">{inputs.publicIPs[key]}</span>
                </div>
                <Slider
                  value={inputs.publicIPs[key]}
                  onChange={(value) => updateIPs(key, value)}
                  min={0}
                  max={20}
                  step={1}
                  label={label}
                  formatValue={(n) => `${n}`}
                />
                <p className="text-[10px] text-outline italic">{note}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Section 4: DDoS Protection ─────────────────────────────── */}
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-4">
            <span className="material-symbols-outlined text-[#a80000]" style={{ fontVariationSettings: "'FILL' 1" }}>security</span>
            DDoS Protection
          </h2>

          <div className="flex flex-col gap-3 mb-4">
            {(
              [
                { value: 'none', label: 'None', sublabel: '£0/month — no DDoS protection beyond basic platform defaults' },
                { value: 'ip_protection', label: 'IP Protection', sublabel: '~£152.6/IP/month — per-protected-public-IP pricing' },
                { value: 'network_protection', label: 'Network Protection', sublabel: '~£2,257/month flat — covers up to 100 public IPs + SLA guarantees' },
              ] as const
            ).map(({ value, label, sublabel }) => (
              <button
                key={value}
                type="button"
                onClick={() => updateDDoS('tier', value)}
                className={`flex items-start gap-3 p-3 rounded text-left border transition-colors ${
                  inputs.ddos.tier === value
                    ? 'bg-primary/10 border-primary/60 text-on-surface'
                    : 'bg-surface-container-lowest border-outline-variant/40 text-outline hover:border-outline-variant'
                }`}
              >
                <span className={`material-symbols-outlined text-[18px] mt-0.5 ${inputs.ddos.tier === value ? 'text-primary' : 'text-outline'}`}>
                  {inputs.ddos.tier === value ? 'radio_button_checked' : 'radio_button_unchecked'}
                </span>
                <div>
                  <p className="text-[13px] font-semibold">{label}</p>
                  <p className="text-[11px] text-outline mt-0.5">{sublabel}</p>
                </div>
              </button>
            ))}
          </div>

          {inputs.ddos.tier === 'ip_protection' && (
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-end">
                <label className="text-[11px] font-mono text-outline uppercase tracking-wider flex items-center">Protected public IPs<FieldTooltip content="IP Protection charges per public IP. At 14+ IPs, Network Protection flat fee becomes cheaper. Each IP must be explicitly enrolled." /></label>
                <span className="text-lg font-bold font-mono text-on-surface">{inputs.ddos.protectedIPCount}</span>
              </div>
              <Slider
                value={inputs.ddos.protectedIPCount}
                onChange={(value) => updateDDoS('protectedIPCount', value)}
                min={1}
                max={50}
                step={1}
                label="Protected public IPs"
                formatValue={(n) => `${n}`}
              />
              <p className="text-[10px] text-outline italic">
                Network Protection becomes cheaper than IP Protection when protecting more than ~15 IPs.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Right column: results ────────────────────────────────────── */}
      <div className="flex flex-col gap-6">
        <div className="sticky top-[72px] flex flex-col gap-6">
          <div id="networking-results" className="bg-surface-container-high micro-gradient border-l-2 border-primary rounded-lg overflow-hidden flex flex-col">
            <div className="p-6">
              <h3 className="font-mono text-[11px] text-outline uppercase tracking-widest mb-2">Estimated Monthly Cost (PAYG)</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold font-headline">
                  {isLoading ? '...' : isError ? 'Error' : `£${calculation?.monthly_estimate?.toFixed(2) ?? '0.00'}`}
                </span>
                {!isLoading && !isError && calculation && <CurrencyTooltip />}
                <span className="text-outline text-sm">/mo</span>
                <button
                  type="button"
                  onClick={() => setFeedbackOpen(true)}
                  title="Report an issue with this estimate"
                  className="ml-1 transition-colors"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  <span className="material-symbols-outlined text-[18px]">help</span>
                </button>
              </div>
              <p className="text-[10px] text-outline mt-1 italic font-mono uppercase tracking-widest">
                UK South · Pay-As-You-Go
              </p>
            </div>

            <div className="bg-surface-container-lowest/50 p-6 space-y-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant flex items-center gap-2">
                <span className="material-symbols-outlined text-sm">list_alt</span>
                Cost Breakdown
              </h4>
              <ul className="space-y-3">
                {calculation?.breakdown?.map((item: { label: string; value: number; isFree?: boolean }, idx: number) => (
                  <li key={idx} className="flex justify-between items-center font-mono text-xs">
                    <span className="text-outline">{item.label}</span>
                    <span className={`font-bold ${item.isFree ? 'text-[#107c10]' : ''}`}>
                      {item.isFree ? 'Free' : `£${item.value.toFixed(4)}`}
                    </span>
                  </li>
                ))}
                {!calculation && !isLoading && (
                  <p className="text-[10px] text-outline text-center py-4">Enable sections above to see pricing</p>
                )}
                {calculation && (
                  <li className="pt-3 border-t border-outline-variant flex justify-between items-center font-mono text-sm">
                    <span className="font-bold">Total</span>
                    <span className="font-bold text-primary">£{calculation.monthly_estimate?.toFixed(2) ?? '0.00'}</span>
                  </li>
                )}
              </ul>
            </div>

            {calculation && (
              <div className="px-6 pb-2">
                <FormulaDisclosure
                  lines={[
                    ...(calculation.breakdown ?? []).map((item: { label: string; value: number }) =>
                      `${item.label}: £${item.value.toFixed(4)}`
                    ),
                    `Region: UK South`,
                  ]}
                  fetchedAt={calculation.prices_last_updated}
                />
              </div>
            )}
          </div>

          {/* Discount modifier */}
          {calculation && (
            <DiscountModifier
              discount={discount}
              onDiscountChange={setDiscount}
              paygTotal={calculation.monthly_estimate ?? 0}
            />
          )}

          {/* NAT Gateway pricing note */}
          <div className="bg-surface-container-lowest/50 border border-outline-variant p-4 flex gap-3">
            <span className="material-symbols-outlined text-outline text-lg shrink-0">info</span>
            <p className="text-[10px] text-outline leading-relaxed italic">
              {calculation?.disclaimer ??
                'NAT Gateway rates sourced from Azure pricing page — not available in the Retail Prices API. All other rates from D1. Estimates only.'}
            </p>
          </div>

          <a href="https://azure.microsoft.com/en-gb/pricing/details/virtual-network/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[10px] font-mono" style={{ color: 'var(--text-tertiary)' }}>
            <span className="material-symbols-outlined text-[13px]">open_in_new</span>
            Verify on official Azure pricing page
          </a>
        </div>
      </div>

      {feedbackOpen && (
        <FeedbackModal
          service="networking"
          estimateValue={calculation ? `£${calculation.monthly_estimate?.toFixed(2)}/mo` : 'N/A'}
          onClose={() => setFeedbackOpen(false)}
        />
      )}

      <MobileCostBar
        amount={calculation?.monthly_estimate ?? null}
        label="Networking"
        targetId="networking-results"
      />
    </div>
  )
}
